/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type { ModuloSezioneDTO, SezioneDTO } from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ottiene l'elenco delle sezioni di un modulo.
 * @summary Elenco sezioni di un modulo
 */
export const getFrontModuliIdSezioni = (
  idModulo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ModuloSezioneDTO[]>(
    {
      url: `/framework-cmp-domande/moduli/${idModulo}/sezioni`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontModuliIdSezioniQueryKey = (idModulo: string) =>
  [`/framework-cmp-domande/moduli/${idModulo}/sezioni`] as const;

export const getGetFrontModuliIdSezioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontModuliIdSezioni>>,
  TError = ErrorType<unknown>,
>(
  idModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontModuliIdSezioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontModuliIdSezioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontModuliIdSezioniQueryKey(idModulo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontModuliIdSezioni>>
  > = ({ signal }) => getFrontModuliIdSezioni(idModulo, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idModulo, ...queryOptions };
};

export type GetFrontModuliIdSezioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontModuliIdSezioni>>
>;
export type GetFrontModuliIdSezioniQueryError = ErrorType<unknown>;

/**
 * @summary Elenco sezioni di un modulo
 */
export const useGetFrontModuliIdSezioni = <
  TData = Awaited<ReturnType<typeof getFrontModuliIdSezioni>>,
  TError = ErrorType<unknown>,
>(
  idModulo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontModuliIdSezioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontModuliIdSezioniQueryOptions(
    idModulo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene il dettaglio della sezione.
 * @summary Dettaglio sezione
 */
export const getFrontModuliIdSezioniId = (
  idModulo: string,
  idSezione: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<SezioneDTO>(
    {
      url: `/framework-cmp-domande/moduli/${idModulo}/sezioni/${idSezione}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontModuliIdSezioniIdQueryKey = (
  idModulo: string,
  idSezione: string,
) =>
  [`/framework-cmp-domande/moduli/${idModulo}/sezioni/${idSezione}`] as const;

export const getGetFrontModuliIdSezioniIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontModuliIdSezioniId>>,
  TError = ErrorType<unknown>,
>(
  idModulo: string,
  idSezione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontModuliIdSezioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontModuliIdSezioniId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontModuliIdSezioniIdQueryKey(idModulo, idSezione);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontModuliIdSezioniId>>
  > = ({ signal }) =>
    getFrontModuliIdSezioniId(idModulo, idSezione, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(idModulo && idSezione),
    ...queryOptions,
  };
};

export type GetFrontModuliIdSezioniIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontModuliIdSezioniId>>
>;
export type GetFrontModuliIdSezioniIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio sezione
 */
export const useGetFrontModuliIdSezioniId = <
  TData = Awaited<ReturnType<typeof getFrontModuliIdSezioniId>>,
  TError = ErrorType<unknown>,
>(
  idModulo: string,
  idSezione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontModuliIdSezioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontModuliIdSezioniIdQueryOptions(
    idModulo,
    idSezione,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
